import { useEffect, useState } from 'react';
import { adminApi } from '../../utils/api';
import { Alert, Badge, Button, Spinner } from './shared';
import CredentialFormModal, { EMPTY_CREDENTIAL } from './CredentialFormModal';

export default function AdminCredentials() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');
    const [filterProvider, setFilterProvider] = useState('');
    const [editing, setEditing] = useState(null);
    const [showForm, setShowForm] = useState(false);

    const load = async () => {
        setLoading(true);
        setErr('');
        try {
            const params = filterProvider ? { provider: filterProvider } : {};
            const rows = await adminApi.listCredentials(params);
            setItems(Array.isArray(rows) ? rows : []);
        } catch (e) {
            setErr(e?.message || String(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [filterProvider]);

    const handleSave = async (payload) => {
        const isUpdate = !!editing && editing.id === payload.id;
        if (isUpdate) {
            await adminApi.updateCredential(payload.id, payload);
        } else {
            await adminApi.createCredential(payload);
        }
        setShowForm(false);
        setEditing(null);
        await load();
    };

    const handleDelete = async (id) => {
        if (!window.confirm(`Delete credential "${id}"?`)) return;
        try {
            await adminApi.deleteCredential(id);
            await load();
        } catch (e) {
            alert(e?.message || String(e));
        }
    };

    const handleEdit = async (id) => {
        const detail = await adminApi.getCredential(id);
        setEditing(detail);
        setShowForm(true);
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">Credentials</h1>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Provider secrets (e.g. GitHub PAT) referenced by sync configs. Tokens are encrypted at rest.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={filterProvider}
                        onChange={(e) => setFilterProvider(e.target.value)}
                    >
                        <option value="">All providers</option>
                        <option value="git">git</option>
                    </select>
                    <Button onClick={() => { setEditing(null); setShowForm(true); }}>+ Add credential</Button>
                </div>
            </div>

            {err && <Alert variant="danger">{err}</Alert>}

            {loading ? (
                <Spinner size="lg" text="Loading credentials..." center />
            ) : items.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">No credentials yet</div>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/60">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Name</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">ID</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Provider</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Repo / Endpoint</th>
                                <th className="text-center px-3 py-2.5 text-xs font-medium text-gray-500">Token</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 w-40">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {items.map((c) => (
                                <tr key={c.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-gray-900">{c.name}</div>
                                        {c.description && <p className="text-xs text-gray-400 mt-0.5">{c.description}</p>}
                                    </td>
                                    <td className="px-3 py-3"><span className="text-xs text-gray-500 font-mono">{c.id}</span></td>
                                    <td className="px-3 py-3"><Badge variant="info">{c.provider}</Badge></td>
                                    <td className="px-3 py-3">
                                        <span className="text-xs text-gray-600 font-mono break-all">{c.provider_data?.repo_url || '-'}</span>
                                    </td>
                                    <td className="px-3 py-3 text-center">
                                        {c.provider_data?.has_token ? <Badge variant="success" dot>set</Badge> : <Badge variant="default">missing</Badge>}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            <Button variant="ghost" size="xs" onClick={() => handleEdit(c.id)}>Edit</Button>
                                            <Button variant="ghost" size="xs" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(c.id)}>Delete</Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <CredentialFormModal
                open={showForm}
                onClose={() => { setShowForm(false); setEditing(null); }}
                initial={editing || EMPTY_CREDENTIAL}
                isExisting={!!editing}
                onSave={handleSave}
            />
        </div>
    );
}
